import { useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Checkbox, DatePicker, Divider, Form, Input } from 'antd';
import { Link, useLoaderData, useNavigate, useRevalidator } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import './admin-license-edit.css';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { showLoadingAlertMessage } from '../../../../../../../utils/show-loading-alert-message/show-loading-alert-message';
import { networkCall } from '../../../../../../../utils/network-call/network-call';
import { showDefaultErrorMessages } from '../../../../../../../utils/show-default-error-message/show-default-error-messages';
import { AlertMessageState, clearAlertMessage, showAlertMessage } from '../../../../../../../redux/reducer/alert-message.reducer';
import { AlertMessages } from '../../../../../../../enums/alert-messages.enum';
import { DefaultErrorResponse } from '../../../../../../../types/api/error/default-error-response/default-error-respose.type';
import { ValidationErrorResponseData } from '../../../../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.';
import { tranformAPIValidationErrorToAntDValidationMessage } from '../../../../../../../utils/validation/transform-api-validation-errors-to-antd-validation-messsages';
import { GetLicenseSuccessResponse } from '../../../../../types/api/get-license-success-response';
import { LicenseTypeEnum } from '../../../../../enums/license-type.enum';
import { Device } from '../../../../../types/api/create-license-success-response';
import { setOpenKeys, setSelectedMenuItemKeys } from '../../../../../../../redux/reducer/antd-layout-info.reducer';
import { getImageUrl } from '../../../../../../../utils/image-helper/get-image-url';
import { CMSButton } from '../../../../../../../components/cms/button/cms-button.';

export const AdminLicenseEdit = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const loaderData: any = useLoaderData();
    const navigate = useNavigate();
    const [devices, setDevices] = useState<Device[]>([]);
    let sendAsFinalized = false;
    const revalidator = useRevalidator();

    const handleFinish = async (values: any) => {
        showLoadingAlertMessage();

        const _devices = [];
        for (const device of devices) {
            _devices.push({
                id: device.id,
                deviceId: values[`devices.${device.id}.deviceId`]
            });
        }

        let startDate: string | undefined = undefined;
        if(values.startDate) {

            const prevStartDate = loaderData.getLicenseResponse.data.license.startDate ? dayjs(new Date(loaderData.getLicenseResponse.data.license.startDate * 1000)) : null;
            if (prevStartDate) {

                if (!prevStartDate.isSame(values.startDate)) startDate = `${ values.startDate.get('year') }-${ values.startDate.get('month') + 1 }-${ values.startDate.get('date') }`;
                
            } else startDate = `${ values.startDate.get('year') }-${ values.startDate.get('month') + 1 }-${ values.startDate.get('date') }`;
        }

        let endDate: string | undefined = undefined;
        if (values.endDate) {

            const prevEndDate = loaderData.getLicenseResponse.data.license.endDate ? dayjs(new Date(loaderData.getLicenseResponse.data.license.endDate * 1000)) : null;
            if (prevEndDate) {
                
                if (!prevEndDate.isSame(values.endDate)) endDate = `${ values.endDate.get('year') }-${ values.endDate.get('month') + 1 }-${ values.endDate.get('date') }`;
                
            } else endDate = `${ values.endDate.get('year') }-${ values.endDate.get('month') + 1 }-${ values.endDate.get('date') }`;
        
        }

        let finalized: boolean | undefined = undefined;
        if (sendAsFinalized === true) finalized = true;

        const response = await networkCall(`/licenses/${ loaderData.licenseId }`, {
            method: 'PUT',
            body: {
                workInstructionModule: values.workInstructionModule,
                processEditorModule: values.processEditorModule,
                modelSpaceModule: values.modelSpaceModule,
                educationModule: values.educationModule,
                startDate,
                endDate,
                finalized,
                devices: _devices
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {

            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
            revalidator.revalidate();
            
        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 1000:
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    alertMessageState.alertMessageText = 'Hibásan megadott beviteli mezők.';
                    const validationErrors = tranformAPIValidationErrorToAntDValidationMessage(_res.data.data!);
                    form.setFields(validationErrors);
                    break;

            }

        } else if (response?.status === 404) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {
                case 13000:
                    alertMessageState.alertMessageText = 'A licenc nem található.';
                    navigate(`/cms/admin/companies/edit/${ loaderData.getLicenseResponse?.data?.license?.company?.id }`);
                    break;
            }

        }

        dispatch(showAlertMessage(alertMessageState));
    }

    const lessThenToday = (currentDate: dayjs.Dayjs) => {
        return currentDate.endOf('day').isBefore(dayjs().startOf('day'));
    };

    const disabledEndDates = (currentDate: dayjs.Dayjs) => {
        if (lessThenToday(currentDate)) return true;

        const startDate = form.getFieldValue('startDate');

        if (startDate && currentDate.endOf('day').isBefore(startDate.startOf('day'))) return true;

        return false;
    };

    useEffect(() => {
        if(loaderData.getLicenseResponse && loaderData.getLicenseResponse?.status === 200) {
            const res: AxiosResponse<GetLicenseSuccessResponse> = loaderData.getLicenseResponse;

            form.setFieldValue('licenseKey', res.data.license.licenseKey);

            let type = '';
            switch (res.data.license.type) {
                case LicenseTypeEnum.Headset:
                    type = 'Headsetes';
                    break;
                case LicenseTypeEnum.Tablet:
                    type = 'Tabletes';
                    break;
                case LicenseTypeEnum.Combined:
                    type = 'Kombinált (Headset + Tablet)';
                    break;
            }
            form.setFieldValue('type', type);
            form.setFieldValue('startDate', loaderData.getLicenseResponse.data.license.startDate ? dayjs(loaderData.getLicenseResponse.data.license.startDate * 1000) : null);
            form.setFieldValue('endDate', loaderData.getLicenseResponse.data.license.endDate ? dayjs(loaderData.getLicenseResponse.data.license.endDate * 1000) : null);
            form.setFieldValue('workInstructionModule', res.data.license.workInstructionModule);
            form.setFieldValue('processEditorModule', res.data.license.processEditorModule);
            form.setFieldValue('modelSpaceModule', res.data.license.modelSpaceModule);
            form.setFieldValue('educationModule', res.data.license.educationModule);
            setDevices(res.data.license?.devices as Device[]);

            return;
        }
    }, [loaderData.getLicenseResponse, form]);

    useEffect(() => {
        dispatch(setOpenKeys(['admin-companies']));
        dispatch(setSelectedMenuItemKeys(['admin-active-companies']));

        return () => {
            dispatch(setOpenKeys([]));
        };
    }, [dispatch]);

    const startDateChange = () => {
        const startDate = form.getFieldValue('startDate') as dayjs.Dayjs;
        if (startDate) form.setFieldValue('endDate', startDate.add(1, 'year'));
    };

    return (
        <>
            <div className="page-title">Licenc módosítása</div>
            <Form
                form={form}
                onFinish={handleFinish}
                layout='vertical'
            >

                <Form.Item
                    label='Licenc kulcs'
                    name='licenseKey'
                >
                    <Input
                        className='box-shadow-4-4-8-0 border-0 fw-bold'
                        disabled
                        suffix={
                            loaderData?.getLicenseResponse?.data?.license?.licenseKey && (
                                <CopyToClipboard
                                    text={loaderData?.getLicenseResponse.data.license.licenseKey}
                                    onCopy={() => {
                                        dispatch(clearAlertMessage());
                                        dispatch(showAlertMessage({
                                            alertMessageType: 'success',
                                            alertMessageText: 'Vágólapra másolva.'
                                        }));
                                    }}
                                >
                                    <img
                                        className='cursor-pointer'
                                        src={getImageUrl('copy-to-clipboard-icon.png')}
                                        alt="copy"
                                        title='Mésolás a vágólapra.'
                                    />
                                </CopyToClipboard>
                            )
                        }
                    />
                </Form.Item>

                <Form.Item
                    label='Típus'
                    name='type'
                >
                    <Input className='box-shadow-4-4-8-0 border-0 fw-bold' disabled />
                </Form.Item>

                <div className='d-flex gap-5'>
                    <Form.Item
                        name="startDate"
                        label="Érvényesség kezdete"
                        rules={[{required: true, message: 'Mező kitöltése kötelező!'}]}
                    >
                        <DatePicker
                            disabledDate={lessThenToday}
                            onChange={startDateChange}
                            className='box-shadow-4-4-8-0 border-0'
                        />
                    </Form.Item>

                    <Form.Item
                        name="endDate"
                        label="Érvényesség vége"
                        rules={[{required: true, message: 'Mező kitöltése kötelező!'}]}
                    >
                        <DatePicker
                            disabledDate={disabledEndDates}
                            className='box-shadow-4-4-8-0 border-0'
                        />
                    </Form.Item>
                </div>
                
                <Divider />

                <div style={{ marginBottom: '6px' }}>
                    <b>Modulok adatai:</b>
                </div>

                <div className="table-responsive">
                    <table className="module-table">
                        <thead>
                            <tr>
                                <th>Megnevezés</th>
                                <th className='text-center'>Elérhető</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Munkautasítás</td>
                                <td className='text-center'>
                                    <Form.Item
                                        name='workInstructionModule'
                                        valuePropName='checked'
                                    >
                                        <Checkbox />
                                    </Form.Item>
                                </td>
                            </tr>
                            <tr>
                                <td>Folyamatszerkesztő</td>
                                <td className='text-center'>
                                    <Form.Item
                                        name='processEditorModule'
                                        valuePropName='checked'
                                    >
                                        <Checkbox />
                                    </Form.Item>
                                </td>
                            </tr>
                            <tr>
                                <td>Modelltér</td>
                                <td className='text-center'>
                                    <Form.Item
                                        name='modelSpaceModule'
                                        valuePropName='checked'
                                    >
                                        <Checkbox />
                                    </Form.Item>
                                </td>
                            </tr>
                            <tr>
                                <td>Oktatás</td>
                                <td className='text-center'>
                                    <Form.Item
                                        name='educationModule'
                                        valuePropName='checked'
                                    >
                                        <Checkbox />
                                    </Form.Item>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Divider />

                {devices.map(device => (
                    <React.Fragment key={device.id}>
                        <div className='mb-2'><b>{device.name} adatai:</b></div>
                        <Form.Item
                            label='Eszköz azonsítója'
                            name={`devices.${device.id}.deviceId`}
                            initialValue={device.deviceId}
                            rules={[{required: true, message: 'Adjon meg azonosítót!'}]}
                        >
                            <Input
                                className='box-shadow-4-4-8-0 border-0'
                                suffix={
                                    device.deviceId && (
                                        <CopyToClipboard
                                            text={device.deviceId}
                                            onCopy={() => {
                                                dispatch(clearAlertMessage());
                                                dispatch(showAlertMessage({
                                                    alertMessageType: 'success',
                                                    alertMessageText: 'Vágólapra másolva.'
                                                }));
                                            }}
                                        >
                                            <img
                                                className='cursor-pointer'
                                                src={getImageUrl('copy-to-clipboard-icon.png')}
                                                alt="copy"
                                                title='Mésolás a vágólapra.'
                                            />
                                        </CopyToClipboard>
                                    )
                                }
                            />
                        </Form.Item>
                    </React.Fragment>
                ))}

                <div className="d-flex justify-content-between mb-4">
                    <Link to={loaderData.getLicenseResponse?.data?.license?.company.id ? `/cms/admin/companies/edit/${ loaderData.getLicenseResponse.data.license.company.id }` :  '/cms/admin/active-companies'}>
                        <CMSButton text='Vissza' type='white' iconSrc={getImageUrl('back-icon.png')} />
                    </Link>
                    <div className='d-flex gap-3'>
                        {!loaderData?.getLicenseResponse?.data?.license?.finalized === true && (
                                <CMSButton
                                    text='Véglegesítés'
                                    type='blue'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (!window.confirm('Biztosan véglegesíted a licencet?')) return;
                                        sendAsFinalized = true;
                                        form.submit();
                                    }}
                                />
                            )
                        }
                        <CMSButton
                            text='Mentés'
                            iconSrc={getImageUrl('Save-icon.png')}
                            htmlType='submit'
                            onClick={(e) => {
                                e.preventDefault();
                                sendAsFinalized = false;
                                form.submit();
                            }}
                        />
                    </div>
                </div>
                        
            </Form>
        </>
    );
};